import React, { useEffect, useMemo, useState } from "react";
import { PlusCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { formatDateTime } from "../lib/utils";

const initialParentForm = {
  phone: "",
  address: "",
};

const initialChildForm = {
  name: "",
  parent_id: "",
  school_id: "",
  pickup_location: "",
  dropoff_location: "",
  notes: "",
};

export default function ParentsPage() {
  const { parents, schools, childrenRecords, loadParents, loadSchools, loadChildren, updateParent, createChild } = useData();
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentForm, setParentForm] = useState(initialParentForm);
  const [childForm, setChildForm] = useState(initialChildForm);

  useEffect(() => {
    Promise.all([loadParents(), loadSchools(), loadChildren()]);
  }, [loadParents, loadSchools, loadChildren]);

  const selectableParents = useMemo(() => parents.filter((entry) => entry.id), [parents]);

  const handleParentEdit = (parent) => {
    setSelectedParent(parent);
    setParentForm({ phone: parent.phone || "", address: parent.address || "" });
  };

  const saveParentProfile = async (event) => {
    event.preventDefault();
    if (!selectedParent) return;
    const result = await updateParent(selectedParent.id, parentForm);
    if (result.success) {
      toast.success("Parent profile updated.");
      loadParents();
    } else {
      toast.error(result.error);
    }
  };

  const createChildRecord = async (event) => {
    event.preventDefault();
    const result = await createChild(childForm);
    if (result.success) {
      toast.success("Child profile created.");
      setChildForm(initialChildForm);
      Promise.all([loadParents(), loadChildren()]);
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid gap-6 xl:grid-cols-2">
        <form onSubmit={saveParentProfile} className="guardian-card bg-white p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Parent profile</h2>
              <p className="mt-1 text-sm text-[#64748B]">Select a parent account and maintain contact details.</p>
            </div>
          </div>
          <div className="mt-6 grid gap-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Parent</label>
              <select value={selectedParent?.id || ""} onChange={(event) => handleParentEdit(selectableParents.find((parent) => parent.id === event.target.value))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="">Select parent</option>
                {selectableParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>{parent.user?.name || parent.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Phone</label>
              <input value={parentForm.phone} onChange={(event) => setParentForm((current) => ({ ...current, phone: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Address</label>
              <input value={parentForm.address} onChange={(event) => setParentForm((current) => ({ ...current, address: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
          </div>
          <button type="submit" disabled={!selectedParent} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
            <Save size={16} /> Save parent profile
          </button>
        </form>

        <form onSubmit={createChildRecord} className="guardian-card bg-white p-6">
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Add child</h2>
            <p className="mt-1 text-sm text-[#64748B]">Link each child to a parent profile and school.</p>
          </div>
          <div className="mt-6 grid gap-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Child name</label>
              <input value={childForm.name} onChange={(event) => setChildForm((current) => ({ ...current, name: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Parent</label>
              <select value={childForm.parent_id} onChange={(event) => setChildForm((current) => ({ ...current, parent_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="">Select parent</option>
                {selectableParents.map((parent) => (
                  <option key={parent.id} value={parent.id}>{parent.user?.name || parent.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">School</label>
              <select value={childForm.school_id} onChange={(event) => setChildForm((current) => ({ ...current, school_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
                <option value="">Select school</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Pickup address</label>
              <input value={childForm.pickup_location} onChange={(event) => setChildForm((current) => ({ ...current, pickup_location: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Dropoff address</label>
              <input value={childForm.dropoff_location} onChange={(event) => setChildForm((current) => ({ ...current, dropoff_location: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0F172A]">Notes</label>
              <textarea value={childForm.notes} onChange={(event) => setChildForm((current) => ({ ...current, notes: event.target.value }))} className="mt-2 min-h-[100px] w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
            </div>
          </div>
          <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
            <PlusCircle size={16} /> Create child profile
          </button>
        </form>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="guardian-card overflow-hidden bg-white">
          <div className="border-b border-[#E2E8F0] px-6 py-4">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Parent accounts</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
                <tr>
                  <th className="px-6 py-3 font-medium">Parent</th>
                  <th className="px-6 py-3 font-medium">Phone</th>
                  <th className="px-6 py-3 font-medium">Children</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {parents.length ? (
                  parents.map((parent) => (
                    <tr key={parent.id} className="border-t border-[#E2E8F0]">
                      <td className="px-6 py-4">
                        <p className="font-medium text-[#0F172A]">{parent.user?.name || "Parent profile"}</p>
                        <p className="mt-1 text-xs text-[#64748B]">{parent.user?.email}</p>
                      </td>
                      <td className="px-6 py-4 text-[#475569]">{parent.phone}</td>
                      <td className="px-6 py-4 text-[#475569]">{parent.child_count || 0}</td>
                      <td className="px-6 py-4 text-[#475569]">{formatDateTime(parent.created_at)}</td>
                      <td className="px-6 py-4">
                        <button onClick={() => handleParentEdit(parent)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">Edit</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-[#64748B]">No parent profiles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="guardian-card overflow-hidden bg-white">
          <div className="border-b border-[#E2E8F0] px-6 py-4">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Children</h2>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {childrenRecords.length ? (
              childrenRecords.map((child) => (
                <div key={child.id} className="p-5 text-sm">
                  <p className="font-semibold text-[#0F172A]">{child.name}</p>
                  <p className="mt-1 text-[#64748B]">Parent: {child.parent_name || "Unassigned"}</p>
                  <p className="text-[#64748B]">School: {child.school_name || "Unassigned"}</p>
                </div>
              ))
            ) : (
              <div className="p-6 text-sm text-[#64748B]">No child records created yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
